/**
 * useContentActions
 *
 * Single, reusable dispatcher for in-place CMS / banner CTAs, triggerable from <Action> panels or
 * from a Braze banner postMessage bridge. Most actions run WITHOUT navigating the user away
 * (modals, add-to-bag, copy-code); `navigate` is the deliberate exception, added for banner CTAs
 * such as "Learn more" that must drive traffic to a landing page (#262226).
 *
 * `dispatch` reports whether the action succeeded so callers can hold back their click event, and it
 * owns how long the caller's campaign attribution stays applied (see ATTRIBUTED_ACTIONS).
 */
import { useToast } from 'vue-toast-notification'
import { isNavigationFailure, NavigationFailureType } from 'vue-router'

export type ContentAction =
  | 'mhj-login-panel'
  | 'mhj-signup-panel'
  | 'setMyStore'
  | 'addToBag'
  | 'navigate'
  | 'copyCode'
  | 'addCoupon'

export type ContentActionParams = {
  productId?: string
  couponCode?: string
  /** Internal path for `navigate`, validated by `isSafeInternalPath` before it reaches the router. */
  url?: string
}

/** Allow-list of actions that may be triggered by an untrusted source (e.g. a banner iframe). */
export const CONTENT_ACTIONS: ContentAction[] = [
  'mhj-login-panel',
  'mhj-signup-panel',
  'setMyStore',
  'addToBag',
  'navigate',
  'copyCode',
  // 'addCoupon' is intentionally NOT exposed yet: the coupon journey is unimplemented (see dispatch
  // below), so a banner click would log but apply nothing. Capability is reserved — re-add here once
  // the coupon flow is built so it can be advertised as a banner/content action again.
]

/**
 * Actions whose click event is held back until `dispatch` reports success (#262226 AC4). The older
 * actions keep logging at click time so their tracking behaviour is unchanged (#262226 AC5).
 */
export const SUCCESS_GATED_ACTIONS: ContentAction[] = ['navigate', 'copyCode']

/**
 * Attribution is global, so it may only outlive `dispatch` for actions whose journey clears it again
 * (modal close, `addToBag`'s finally). Terminal actions clear it rather than leak it into later events.
 */
const ATTRIBUTED_ACTIONS: ContentAction[] = ['mhj-login-panel', 'mhj-signup-panel', 'setMyStore', 'addToBag']

/**
 * Subset of CONTENT_ACTIONS safe to trigger from a Braze IAM extras KVP. `addToBag` stays excluded —
 * its `productId` can't be reliably sourced from a static KVP, unlike `navigate`/`copyCode`.
 */
export const IAM_CONTENT_ACTIONS: ContentAction[] = [
  'mhj-login-panel',
  'mhj-signup-panel',
  'setMyStore',
  'navigate',
  'copyCode',
]

export function useContentActions() {
  const uiStore = useUIStore()
  const productStore = useProductStore('quickview')
  const { addToBagAction } = useCartActions()
  const { setCampaign, clearCampaign } = useCampaignAttribution()

  const addToBagLoading = ref(false)

  async function addToBag(productId?: string) {
    let opensQuickview = false
    try {
      addToBagLoading.value = true
      if (!productId) throw new Error('addToBag, no productId provided')
      await productStore.getNewVariant(productId)
      if (!productStore.selectedProduct) throw new Error('addToBag, no product found')
      if (!productStore.selectedProduct?.c_mHJRetailCategory.includes('Rings')) {
        await addToBagAction(
          {
            productId: productStore.selectedProduct.id,
            popup: {
              selectedPcpOption: null,
              product: productStore.selectedProduct,
            },
          },
          {
            product: productStore.selectedProduct,
            group: 'Content Action',
            location: 'Content Action',
          },
        )
        return
      }
      productStore.applyDefaultRingSize()
      uiStore.isShowQuickviewModal = true
      opensQuickview = true
    } catch (e) {
      console.error(e)
    } finally {
      addToBagLoading.value = false
      if (!opensQuickview) clearCampaign()
    }
  }

  /**
   * Re-validates the path even though the banner bridge already sanitised it: `dispatch` is reachable
   * from other untrusted surfaces (IAM extras), and an open redirect is not worth the single check.
   */
  async function navigate(url?: string): Promise<boolean> {
    if (!url) return false
    if (!isSafeInternalPath(url)) {
      // Likely an absolute URL authored by mistake — fails closed with no click logged, so surface it.
      if (import.meta.dev) console.warn(`[useContentActions] navigate ignored a non-relative url: "${url}"`)
      return false
    }
    try {
      // Router navigation keeps the JS context alive, so the queued banner_click still reaches Braze.
      const failure = await navigateTo(url)
      if (failure === false) return false
      // A duplicated navigation means the user is already where the CTA points, so the click counts.
      if (isNavigationFailure(failure)) return isNavigationFailure(failure, NavigationFailureType.duplicated)
      return true
    } catch {
      return false
    }
  }

  async function copyCode(couponCode?: string): Promise<boolean> {
    if (!couponCode) return false
    // Resolved before the await: `useNuxtApp()` throws once the async context is lost.
    const { t } = useNuxtApp().$i18n
    const toast = useToast()
    const copied = await writeToClipboard(couponCode)
    if (copied) {
      toast.success(t('actions.copy_code_success', { code: couponCode }), { duration: 3000, position: 'top' })
    } else {
      toast.error(t('actions.copy_code_error'), { duration: 5000, position: 'top' })
    }
    return copied
  }

  /** @returns whether the action completed successfully. */
  async function dispatch(
    action: ContentAction,
    params: ContentActionParams = {},
    campaign?: CampaignDataLayer | null,
  ): Promise<boolean> {
    // `undefined` means the caller does not manage attribution and whatever is set stays untouched.
    if (campaign !== undefined) {
      if (ATTRIBUTED_ACTIONS.includes(action)) setCampaign(campaign)
      else clearCampaign()
    }

    switch (action) {
      case 'mhj-login-panel':
        uiStore.isShowLoginModal = true
        return true
      case 'mhj-signup-panel':
        uiStore.isShowSignUpModal = true
        return true
      case 'setMyStore':
        uiStore.isShowSetMyStoreModal = true
        return true
      case 'addToBag':
        await addToBag(params.productId)
        return true
      case 'navigate':
        return navigate(params.url)
      case 'copyCode':
        return copyCode(params.couponCode)
      case 'addCoupon':
        // Reserved for future use. The coupon journey is a complicated scenario that needs a user
        // journey review before implementation, so this is a deliberate no-op and `addCoupon` is kept
        // out of CONTENT_ACTIONS above until then. Do not advertise this as a banner action yet.
        // if (params.couponCode) await applyCoupon(params.couponCode)
        return false
    }
  }

  return { dispatch, addToBagLoading }
}
